import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { 
  getAnalysisSessions, 
  getSessionResults, 
  deleteSession, 
  createNewSession 
} from '../services/sessionService';
import { formatTimeAgo } from '../services/reportGenerator';

const { FiClock, FiTrash2, FiFolder, FiPlus, FiX, FiSave, FiRefreshCw, FiServer, FiMail } = FiIcons;

const SessionManager = ({ onSessionSelect, currentSessionId, onSessionsLoaded }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [newSessionName, setNewSessionName] = useState('');
  const [error, setError] = useState(null);

  // Load sessions
  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('Loading sessions...');
      const sessionsData = await getAnalysisSessions();
      console.log('Sessions loaded:', sessionsData);
      setSessions(sessionsData || []);
      
      // Notify parent component about loaded sessions
      if (onSessionsLoaded) {
        onSessionsLoaded(sessionsData || []);
      }
    } catch (err) {
      console.error('Error loading sessions:', err);
      setError('Failed to load sessions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSessionSelect = async (sessionId) => {
    if (onSessionSelect && sessionId) {
      setLoading(true);
      setError(null);
      try {
        console.log('Loading session results for:', sessionId);
        const results = await getSessionResults(sessionId);
        console.log(`Loaded ${results?.length || 0} results`);
        
        // Pass the results and session ID to the parent component
        onSessionSelect(sessionId, results);
      } catch (err) {
        console.error('Error loading session results:', err);
        setError('Failed to load session data. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeleteSession = async (e, sessionId) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this session?')) {
      setLoading(true);
      setError(null);
      try {
        const result = await deleteSession(sessionId);
        if (result.success) {
          await loadSessions();
        } else {
          setError('Failed to delete session');
        }
      } catch (err) {
        console.error('Error deleting session:', err);
        setError('Failed to delete session. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCreateSession = async () => {
    if (!newSessionName.trim()) {
      alert('Please enter a session name');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Create new session
      console.log('Creating new session:', newSessionName.trim());
      const result = await createNewSession(newSessionName.trim());
      
      if (result.success) {
        // Refresh sessions list
        console.log('Session created successfully:', result);
        await loadSessions();
        
        // Pass empty results array and session ID to parent
        if (onSessionSelect) {
          onSessionSelect(result.sessionId, [], newSessionName.trim());
        }
        
        setNewSessionName('');
        setShowModal(false);
      } else {
        setError('Failed to create new session');
      }
    } catch (err) {
      console.error('Error creating session:', err);
      setError('Failed to create session. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadSessions();
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      className="session-manager"
    >
      <div className="session-header">
        <h3>Analysis Sessions</h3>
        <div className="session-actions">
          <button 
            className="refresh-button" 
            onClick={handleRefresh}
            title="Refresh sessions"
            disabled={loading}
          >
            <SafeIcon icon={FiRefreshCw} className={loading ? "spinning" : ""} />
          </button>
          <button 
            className="new-session-button" 
            onClick={() => setShowModal(true)}
            disabled={loading}
          >
            <SafeIcon icon={FiPlus} />
            <span>New Session</span>
          </button>
        </div>
      </div>
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      <div className="sessions-list">
        {loading ? (
          <div className="loading-state">
            <SafeIcon icon={FiRefreshCw} className="spinning" />
            <span>Loading sessions...</span>
          </div>
        ) : sessions.length > 0 ? (
          sessions
            .filter(session => !(session.localOnly && session.session_type === 'email_verification'))
            .map((session) => (
            <div 
              key={session.id} 
              className={`session-item ${session.id === currentSessionId ? 'active' : ''}`}
              onClick={() => handleSessionSelect(session.id)}
            >
              <div className="session-icon">
                <SafeIcon icon={
                  session.session_type === 'email_verification' ? FiMail :
                  session.localOnly ? FiServer : FiFolder
                } />
              </div>
              <div className="session-details">
                <div className="session-name">
                  {session.name}
                  {session.localOnly && (
                    <span className="local-badge" title="Stored locally">Local</span>
                  )}
                  {session.session_type === 'email_verification' && (
                    <span className="session-type-badge" title="Email Verification">Email</span>
                  )}
                </div>
                <div className="session-meta">
                  <SafeIcon icon={FiClock} className="meta-icon" />
                  <span>{formatTimeAgo(session.timestamp)}</span>
                  {session.session_type === 'email_verification' ? (
                    <span className="email-count">
                      {session.verified_leads || 0} verified, {session.safe_leads || 0} safe
                    </span>
                  ) : (
                    <span className="website-count">{session.website_count} websites</span>
                  )}
                </div>
              </div>
              <button 
                className="delete-session" 
                onClick={(e) => handleDeleteSession(e, session.id)}
                title="Delete session"
              >
                <SafeIcon icon={FiTrash2} />
              </button>
            </div>
          ))
        ) : (
          <div className="empty-state">
            <p>No sessions found</p>
            <button 
              className="create-session-button" 
              onClick={() => setShowModal(true)}
              disabled={loading}
            >
              Create your first session
            </button>
          </div>
        )}
      </div>
      
      {/* New Session Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Create New Session</h2>
            <p className="modal-description">
              Create a new analysis session to organize your website analyses
            </p>
            <div className="form-group">
              <label>Session Name</label>
              <input 
                type="text" 
                value={newSessionName}
                onChange={(e) => setNewSessionName(e.target.value)}
                placeholder="E.g., Client Website Analysis"
                className="form-input"
                autoFocus
              />
            </div>
            <div className="modal-actions">
              <button className="cancel-button" onClick={() => setShowModal(false)}>
                <SafeIcon icon={FiX} />
                <span>Cancel</span>
              </button>
              <button 
                className="create-button" 
                onClick={handleCreateSession}
                disabled={loading}
              >
                <SafeIcon icon={loading ? FiRefreshCw : FiSave} className={loading ? "spinning" : ""} />
                <span>{loading ? "Creating..." : "Create Session"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx="true">{`
        .session-manager {
          margin-bottom: 2rem;
        }
        .session-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }
        .session-header h3 {
          font-size: 1.125rem;
          font-weight: 600;
          margin: 0;
        }
        .session-actions {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .refresh-button {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          background-color: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          cursor: pointer;
        }
        .refresh-button:hover {
          background-color: #f9fafb;
        }
        .new-session-button {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background-color: var(--primary-color);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
        }
        .new-session-button:hover {
          background-color: var(--primary-dark);
        }
        .new-session-button:disabled,
        .refresh-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        .error-message {
          padding: 0.75rem;
          background-color: #fee2e2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          color: #b91c1c;
          font-size: 0.875rem;
          margin-bottom: 1rem;
        }
        .sessions-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        .session-item {
          display: flex;
          align-items: center;
          padding: 0.75rem 1rem;
          background-color: white;
          border-radius: 8px;
          box-shadow: var(--shadow-sm);
          cursor: pointer;
          transition: all 0.2s;
          border: 1px solid transparent;
        }
        .session-item:hover {
          box-shadow: var(--shadow-md);
        }
        .session-item.active {
          border-color: var(--primary-color);
          background-color: #f5f3ff;
        }
        .session-icon {
          width: 40px;
          height: 40px;
          background-color: #f3f4f6;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 1rem;
          color: var(--primary-color);
        }
        .active .session-icon {
          background-color: #e0e7ff;
        }
        .session-details {
          flex: 1;
          overflow: hidden;
        }
        .session-name {
          font-weight: 500;
          margin-bottom: 0.25rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .local-badge {
          font-size: 0.625rem;
          background-color: #f3f4f6;
          color: var(--text-light);
          padding: 0.125rem 0.375rem;
          border-radius: 4px;
        }
        .session-meta {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.75rem;
          color: var(--text-light);
        }
        .meta-icon {
          font-size: 0.75rem;
        }
        .website-count {
          margin-left: auto;
        }
        .delete-session {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: none;
          border: none;
          color: var(--text-light);
          cursor: pointer;
          margin-left: 0.5rem;
        }
        .delete-session:hover {
          background-color: #fee2e2;
          color: #b91c1c;
        }
        .empty-state {
          padding: 2rem;
          text-align: center;
          background-color: white;
          border-radius: 8px;
          box-shadow: var(--shadow-sm);
          color: var(--text-light);
        }
        .create-session-button {
          margin-top: 0.75rem;
          padding: 0.5rem 1rem;
          background-color: var(--primary-color);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
        }
        .loading-state {
          padding: 2rem;
          text-align: center;
          background-color: white;
          border-radius: 8px;
          box-shadow: var(--shadow-sm);
          color: var(--text-light);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
        }
        .spinning {
          animation: spin 1.5s linear infinite;
          font-size: 1.5rem;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .modal-content {
          background-color: white;
          border-radius: 8px;
          padding: 1.5rem;
          width: 100%;
          max-width: 500px;
          box-shadow: var(--shadow-lg);
        }
        .modal-content h2 {
          margin-bottom: 0.5rem;
          font-size: 1.25rem;
          font-weight: 600;
        }
        .modal-description {
          color: var(--text-light);
          font-size: 0.875rem;
          margin-bottom: 1.5rem;
        }
        .form-group {
          margin-bottom: 1rem;
        }
        .form-group label {
          display: block;
          margin-bottom: 0.5rem;
          font-size: 0.875rem;
          font-weight: 500;
        }
        .form-input {
          width: 100%;
          padding: 0.5rem;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          font-size: 0.875rem;
        }
        .modal-actions {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          margin-top: 1.5rem;
        }
        .cancel-button {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          border: 1px solid #e5e7eb;
          background-color: white;
          border-radius: 8px;
          font-size: 0.875rem;
          cursor: pointer;
        }
        .create-button {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background-color: var(--primary-color);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
        }
        .create-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
      `}</style>
    </motion.div>
  );
};

export default SessionManager;